import { ChangeEvent, useEffect, useState } from "react";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { returnPurchaseOrders } from "../../services";
import { PurchaseOrderListItem } from "../../types/purchase-order.type";
import { numberWithCommas } from "../../utils";
import { pushGlobalSuccess } from "../../success/globalSuccess";
import ConfirmModal from "../ConfirmModal";
import { customerRefText, formatCalendarDate, PO_EMPTY_TEXT } from "./data";
import {
  buildReturnReason,
  RETURN_REASON_MAX_LENGTH,
  RETURN_REASON_OTHER,
  RETURN_REASON_PRESETS,
} from "./returnReason";

interface ReturnDialogProps {
  open: boolean;
  /** ใบที่จะส่งคืน — ผู้เรียกกรองให้เหลือเฉพาะใบที่ส่งคืนได้ (พร้อมใช้) แล้ว */
  purchaseOrders: PurchaseOrderListItem[];
  onClose: () => void;
  onReturned: () => void;
}

/**
 * W7 ส่งคืน HO — สาขาบอก HO ว่า PO ชุดนี้ไม่ใช่งานของสาขา พร้อมเหตุผลที่บังคับกรอก
 *
 * ใบที่ส่งคืนยังถือสาขาและร้านเดิม แต่ถูกดึงออกจากสาขาทันที (เซลส์เลือกไม่ได้แล้ว) และไป
 * รอที่ HO ในสถานะ สาขาส่งคืน · 409 (ใช้แล้ว / หมดช่วงใช้ / ไม่ใช่ของสาขานี้) แจ้งด้วย
 * ข้อความไทยจาก API ผ่าน interceptor
 */
export default function ReturnDialog({
  open,
  purchaseOrders,
  onClose,
  onReturned,
}: ReturnDialogProps) {
  const [preset, setPreset] = useState<string | null>(null);
  const [detail, setDetail] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);

  // ร่างเริ่มใหม่ทุกครั้งที่เปิด
  useEffect(() => {
    if (!open) return;
    setPreset(null);
    setDetail("");
  }, [open]);

  const reason = buildReturnReason(preset, detail);
  const needsDetail = preset === RETURN_REASON_OTHER || preset === null;

  const handleChangeDetail = (event: ChangeEvent<HTMLInputElement>) => {
    setDetail(event.target.value);
  };

  const handleConfirm = async () => {
    if (!reason || purchaseOrders.length === 0) return;

    setSaving(true);
    try {
      const {
        data: { data },
      } = await returnPurchaseOrders({
        uuids: purchaseOrders.map((row) => row.uuid),
        reason,
      });
      if (data) {
        pushGlobalSuccess(
          `ส่งคืน HO แล้ว ${numberWithCommas(data.returned_count)} ใบ`,
        );
      }
      onReturned();
      onClose();
    } catch {
      // interceptor แจ้งข้อความจาก API แล้ว · คง dialog ไว้ให้แก้แล้วลองใหม่
    } finally {
      setSaving(false);
    }
  };

  return (
    <ConfirmModal
      title="ส่งคืน HO"
      description={`ส่งคืน PO ${numberWithCommas(purchaseOrders.length)} ใบ ให้ HO จัดการต่อ — ใบเหล่านี้จะถูกดึงออกจากสาขาทันที`}
      open={open}
      loading={saving}
      confirmLabel="ส่งคืน HO"
      confirmColor="warning"
      confirmDisabled={!reason || purchaseOrders.length === 0}
      onClose={onClose}
      onConfirm={handleConfirm}
    >
      <Stack mt={2} gap={2}>
        <TableContainer sx={{ maxHeight: 240 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>เลข PO</TableCell>
                <TableCell>ร้าน</TableCell>
                <TableCell>วันส่ง</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {purchaseOrders.map((row) => (
                <TableRow key={row.uuid}>
                  <TableCell>{row.po_number}</TableCell>
                  <TableCell>
                    {customerRefText(row.customer) ||
                      row.ship_to_name ||
                      PO_EMPTY_TEXT}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {formatCalendarDate(row.delivery_date)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack gap={1}>
          <Typography variant="body2" fontWeight={600}>
            เหตุผล (บังคับ)
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {RETURN_REASON_PRESETS.map((option) => (
              <Chip
                key={option}
                label={option}
                color={preset === option ? "warning" : "default"}
                variant={preset === option ? "filled" : "outlined"}
                onClick={() => setPreset(preset === option ? null : option)}
              />
            ))}
          </Stack>
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            label={needsDetail ? "ระบุเหตุผล" : "รายละเอียดเพิ่มเติม (ไม่บังคับ)"}
            value={detail}
            onChange={handleChangeDetail}
            error={detail.length > RETURN_REASON_MAX_LENGTH}
            helperText={
              preset === RETURN_REASON_OTHER && !detail.trim()
                ? "เลือก อื่นๆ ต้องพิมพ์เหตุผล"
                : `${detail.length}/${RETURN_REASON_MAX_LENGTH}`
            }
          />
        </Stack>
      </Stack>
    </ConfirmModal>
  );
}
